import { Link } from 'react-router'
import { Button, Stack, TextInput, Text, Alert, Anchor } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react'
import AuthLayout from './AuthLayout'
import Footer from '../common/components/footer/footer'
import {
  ForgotPasswordForm,
  ForgotPasswordSchema,
  useRequestPasswordReset,
} from './api'
import Meta from '../common/Meta'
import { ReactComponent as LeaguestarLogo } from '../assets/leaguestar_logo.svg'
import classes from './Auth.module.css'
import { Typography } from '../common/typography'

export default function ForgotPasswordPage() {
  const {
    mutate: requestPasswordReset,
    isPending,
    error,
    isSuccess,
  } = useRequestPasswordReset()

  const form = useForm<ForgotPasswordForm>({
    initialValues: emptyResetPasswordForm,
    validate: zodResolver(ForgotPasswordSchema),
  })

  const handleSubmit = (values: ForgotPasswordForm) => {
    requestPasswordReset(values, {
      onSuccess: () => {
        form.reset()
      },
    })
  }

  return (
    <AuthLayout>
      <Meta title="Forgot password" />
      <div className={classes.auth_container}>
        <div className={classes.auth_wrapper}>
          <LeaguestarLogo className={classes.leaguestar_logo} />

          {isSuccess ? (
            <Stack className={classes.form_container} align="center" gap="md">
              <Alert
                icon={<IconCircleCheck />}
                color="green"
                classNames={{
                  root: classes.alert_root,
                  wrapper: classes.alert_wrapper,
                  message: classes.alert_message,
                  icon: classes.check_icon,
                }}
              >
                Password reset email sent! <br />
                Please check your inbox.
              </Alert>
              <Text className={classes.info_text}>
                If you don't receive an email within a few minutes, please check
                your spam folder.
              </Text>
              <Button component={Link} to="/login" className={classes.button}>
                Return to login
              </Button>
            </Stack>
          ) : (
            <div className={classes.auth_container}>
              <form
                className={classes.form_container}
                onSubmit={form.onSubmit(handleSubmit)}
                noValidate
              >
                <Stack justify="center">
                  {error && (
                    <Alert icon={<IconAlertCircle size="1rem" />} color="red">
                      {error.message}
                    </Alert>
                  )}
                  <TextInput
                    placeholder="Email"
                    type="email"
                    classNames={{
                      error: classes.error,
                      input: classes.input,
                      wrapper: classes.wrapper,
                    }}
                    {...form.getInputProps('email')}
                    disabled={isPending}
                  />
                  <Button
                    className={classes.button}
                    type="submit"
                    loading={isPending}
                    disabled={isPending}
                  >
                    Reset password
                  </Button>
                </Stack>
              </form>
              <Anchor
                component={Link}
                to="/login"
                underline="always"
                style={{ textDecorationColor: 'white' }}
              >
                <Typography
                  fs="sm"
                  fc={{ light: 'white', dark: 'white' }}
                  lh="140"
                  ta="center"
                >
                  Back to Login
                </Typography>
              </Anchor>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </AuthLayout>
  )
}

const emptyResetPasswordForm: ForgotPasswordForm = {
  email: '',
}
